import React from "react";
import { loadedAsyncState, loadingAsyncState, route, Widget } from "widgets-for-react";
import { ExtendedModuleState, Module } from "../../../Shared/Frontend/application-discovery/module";
import { Updater } from "widgets-for-react";
import { PageParams } from "../../../Shared/Frontend/utils/routes.shared";
import { CMSEnvironment, initCMSBlocksState, loadCMSBlocksState, loadPreviewBlockState } from "../cms/cmsPage.state";
import { cmsWidget } from "../cms/cmsPage.widget";
import { initialSearchPageState, SearchPageState } from "../cms/customPages/overviewPage/searchPage/searchPage.state";
import { storyOverviewPageState, StoryOverviewPageState } from "../cms/customPages/overviewPage/storyOverviewPage/storyOverviewPage.state";
import { searchBarStateZero } from "../cms/searchBar/searchBar.state";
import { makeProjectCookieStateZero } from "../cookiebar/cookiebar.state";
import { publicSiteHeader } from "./publicSite.header";
import { ModuleState, publicSiteModuleUpdaters, StateRequirements } from "./publicSite.module.state";
import { initCMSHeaderState } from "../cms/header/siteHeader/cmsSiteHeader.state";
import { newsOverviewPageState, NewsOverviewPageState } from "../cms/customPages/overviewPage/newsOverviewPage/newsOverviewPage.state";
import { widget404 } from "../cms/customPages/404/404.widget";
import { initCMSFooterState } from "../cms/footer/cmsFooter.state";
import { initPopupState } from "../cms/popup/popup.state";
import { loadPreviewPopups } from "../cms/popup/popup.api";
import { newsletterPageState, NewsletterPageState } from "../cms/customPages/newsletterPage/newsletter.state";
import { StoryPageState, storyPageState } from "../cms/customPages/storyPage/storyPage.state";

export const isDTF_ExampleApplication = false //not necessary, but set this to false when switching to an actual project
export const isDTF_WP = true //not necessary, but set this to false when switching to an actual project}

// export const cmsEnvironmentOptions: CMSEnvironment["environmentOptions"] // List of all the available Modern Block adapters
export const cmsEnvironment: CMSEnvironment["environment"] = "drupal" // Change this to change to a different CMS backend

export type PagesRequirements = {}

export type ModulePages = {
  preview: { url: ['node-preview', { nodeId: number }, { revisionId: string }, { token: string }] }
  drupal: { url: ["", { slug: string }] }
  newsletter: { url: ["nieuwsbrief", { email: string }] }
  search: { url: ["zoeken", { searchQuery: string }] }
  storyOverview: { url: ['verhalen'] }
  story: { url: ["verhalen", { slug: string }] }
  newsOverview: { url: ['nieuws'] }
}

export type CustomPage = {
  widget: (state: ModuleState) => Widget<Updater<ModuleState>>
  header?: (state: ModuleState) => Widget<Updater<ModuleState>>
  breadcrumbs?: (state: ModuleState) => Widget<Updater<ModuleState>>
}

export type CustomPages = {
  newsletterPageState: NewsletterPageState
  searchPageState: SearchPageState
  storyOverviewPageState: StoryOverviewPageState
  storyPageState: StoryPageState
  newsOverviewPageState: NewsOverviewPageState
  "404": CustomPage
}

export const publicSiteModule: Module<ModulePages, ModuleState, StateRequirements, PagesRequirements> = {
  new: <allApplicationState extends ModuleState & StateRequirements>() => {
    type Pages = ModulePages & PagesRequirements
    type State = ExtendedModuleState<allApplicationState & ModuleState & StateRequirements, Pages>
    const updaters = publicSiteModuleUpdaters<allApplicationState>()

    return ({
      routeBuilders: () => ({
        preview: {
          make: (params: PageParams<ModulePages['preview']>) =>
            ({ kind: 'preview', params: params })
        },
        drupal: {
          make: (params: PageParams<ModulePages["drupal"]>) =>
            ({ kind: "drupal", params: params })
        },
        newsletter: {
          make: (params: PageParams<ModulePages["newsletter"]>) =>
            ({ kind: "newsletter", params: params })
        },
        search: {
          make: (params: PageParams<ModulePages["search"]>) =>
            ({ kind: "search", params: params })
        },
        storyOverview: {
          make: (params: PageParams<ModulePages["storyOverview"]>) =>
            ({ kind: "storyOverview", params: params })
        },
        story: {
          make: (params: PageParams<ModulePages["story"]>) =>
            ({ kind: "story", params: params })
        },
        newsOverview: {
          make: (params: PageParams<ModulePages["newsOverview"]>) =>
            ({ kind: "newsOverview", params: params })
        },
      }),
      routeUpdaters: function (this) {
        return ({
          preview: {
            jumpTo:
              (
                params: PageParams<ModulePages['preview']>
              ): Updater<
                ExtendedModuleState<allApplicationState & ModuleState & StateRequirements,
                  ModulePages & PagesRequirements
                >
              > =>
                (s0) => ({
                  ...s0,
                  previousSlug: s0.page.kind == "drupal" ? s0.page.params.slug : undefined,
                  isCustomPage: "none",
                  page: this.routeBuilders().preview.make(params),
                  rootModernBlocksState: loadPreviewBlockState(cmsEnvironment, params.nodeId, params.revisionId, params.token),
                  popupState: { ...s0.popupState, preview: true, popups: loadingAsyncState(() => loadPreviewPopups()) },
                  CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
                }),
                url: '/node-preview/:nodeId([0-9]+)/:revisionId([0-9a-zA-Z]+)',
          },
          drupal: {
            jumpTo: (
              params: PageParams<ModulePages["drupal"]>
            ): Updater<
              ExtendedModuleState<allApplicationState & ModuleState & StateRequirements,
                ModulePages & PagesRequirements
              >
            > => s0 => {
              //   ({ ...s0, drupalState: drupalStateUpdaters.ensureLoading(s0.drupalState), page: this.routeBuilders().drupal.make(params) }),
              // url: "/spa/drupal/:slug+"
              let urlToFetch: string = params.slug
              urlToFetch = urlToFetch || ""
              if (s0.previousSlug != params.slug) { window.scrollTo(0, 0) }
              return {
                ...s0,
                previousSlug: s0.page.kind == "drupal" ? params.slug : undefined,
                isCustomPage: "none",
                page: this.routeBuilders().drupal.make(params),
                rootModernBlocksState: loadCMSBlocksState(cmsEnvironment, urlToFetch),
                CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
              }
            },
            url: "/:slug(.*?)?"
          },
          newsletter: {
            jumpTo: (params: PageParams<ModulePages["newsletter"]>): Updater<ExtendedModuleState<allApplicationState & ModuleState & StateRequirements, ModulePages & PagesRequirements>> => s0 => {
              let urlToFetch: string = "nieuwsbrief"
              window.scrollTo(0, 0)
              return {
                ...s0,
                isCustomPage: "newsletterPageState",
                page: this.routeBuilders().newsletter.make(params),
                newsletterPageState: newsletterPageState(params.email),
                rootModernBlocksState: loadCMSBlocksState(cmsEnvironment, urlToFetch),
                CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
              }
            },
            url: "/nieuwsbrief"
          },
          search: {
            jumpTo: (params: PageParams<ModulePages["search"]>): Updater<ExtendedModuleState<allApplicationState & ModuleState & StateRequirements, ModulePages & PagesRequirements>> => s0 => {
              window.scrollTo(0, 0)
              return {
                ...s0,
                // prevPage:s0., 
                isCustomPage: "searchPageState",
                searchPageState: initialSearchPageState(),
                page: this.routeBuilders().search.make(params),
                CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
              }
            },
            url: "/zoeken"
          },
          storyOverview: {
            jumpTo: (params: PageParams<ModulePages["storyOverview"]>): Updater<ExtendedModuleState<allApplicationState & ModuleState & StateRequirements, ModulePages & PagesRequirements>> => s0 => {
              let urlToFetch: string = "verhalen"
         
              window.scrollTo(0, 0)
              return {
                ...s0,
                // prevPage:s0., 
                isCustomPage: "storyOverviewPageState",
                searchPageState: storyOverviewPageState(),
                page: this.routeBuilders().storyOverview.make(params),
                rootModernBlocksState: loadCMSBlocksState(cmsEnvironment, urlToFetch),
                CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
              }
            },
            url: "/verhalen"
          },
          story: {
            jumpTo: (params: PageParams<ModulePages["story"]>): Updater<ExtendedModuleState<allApplicationState & ModuleState & StateRequirements, ModulePages & PagesRequirements>> => s0 => {
              let urlToFetch: string = `verhalen/${params.slug}`
              const res = loadCMSBlocksState(cmsEnvironment, urlToFetch)
              window.scrollTo(0, 0)
              return {
                ...s0,
                // prevPage:s0., 
                isCustomPage: "storyPageState",
                storyPageState: storyPageState(urlToFetch),
                page: this.routeBuilders().story.make(params),
                rootModernBlocksState: res,
                CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
              }
            },
            url: "/verhalen/:slug"
          },
          newsOverview: {
            jumpTo: (params: PageParams<ModulePages["newsOverview"]>): Updater<ExtendedModuleState<allApplicationState & ModuleState & StateRequirements, ModulePages & PagesRequirements>> => s0 => {
              let urlToFetch: string = "nieuws"
              window.scrollTo(0, 0)
              return {
                ...s0,
                // prevPage:s0., 
                isCustomPage: "newsOverviewPageState",
                searchPageState: newsOverviewPageState(),
                page: this.routeBuilders().newsOverview.make(params),
                rootModernBlocksState: loadCMSBlocksState(cmsEnvironment, urlToFetch),
                CMSHeaderState: { ...s0.CMSHeaderState, isMenuOpen: false, searchState: searchBarStateZero() }
              }
            },
            url: "/nieuws"
          },
        })
      },
      routeWidgetsObject: function (this) {
        return ({
          drupal: route(this.routeUpdaters().drupal.url, this.routeUpdaters().drupal.jumpTo),
          preview: route(this.routeUpdaters().preview.url, this.routeUpdaters().preview.jumpTo),
          search: route(this.routeUpdaters().search.url, this.routeUpdaters().search.jumpTo),
          newsletter: route(this.routeUpdaters().newsletter.url, this.routeUpdaters().newsletter.jumpTo),
          storyOverview: route(this.routeUpdaters().storyOverview.url, this.routeUpdaters().storyOverview.jumpTo),
          story: route(this.routeUpdaters().story.url, this.routeUpdaters().story.jumpTo),
          newsOverview: route(this.routeUpdaters().newsOverview.url, this.routeUpdaters().newsOverview.jumpTo),
        })
      },
      routeWidgets: function (this) {
        return [
          this.routeWidgetsObject().preview,
          this.routeWidgetsObject().storyOverview,
          this.routeWidgetsObject().story,
          this.routeWidgetsObject().newsOverview,
          this.routeWidgetsObject().search,
          this.routeWidgetsObject().newsletter,
          this.routeWidgetsObject().drupal,
        ]
      },
      tryPageSwitch: (currentState: State)
        : Widget<Updater<State>> | undefined => {
        return cmsWidget(currentState).map(u => s0 => ({ ...s0, ...u(s0) }))
      },
      headerWidget: (currentState: State)
        : Widget<Updater<State>> =>
        publicSiteHeader(currentState),
    })
  },
  initialState: () => ({
    isCustomPage: "none",
    previousSlug: undefined,
    popupState: initPopupState(),
    rootModernBlocksState: initCMSBlocksState(cmsEnvironment),
    CMSHeaderState: initCMSHeaderState(),
    CMSFooterState: initCMSFooterState(),
    cookiebarState: makeProjectCookieStateZero(),
    searchPageState: initialSearchPageState(),
    newsletterPageState: newsletterPageState(),
    storyOverviewPageState: storyOverviewPageState(),
    storyPageState: storyPageState(""),
    newsOverviewPageState: newsOverviewPageState(),
    404: { widget: widget404 }
  })
}

